import config from '../../config';
import deepCopy from '../../utils/deepCopy';
import isMobile from '../../utils/isMobile';
import type { ActionsPaneDimensions, WebinarDimensions } from './context';
import { staticWebinarDimensions } from './staticWebinarDimensions';
import { isPortraitOrientation } from '../../shared/frontend/utils/window-browser';
import {
  PlayerMode,
  VideoDimensions,
  VideoPlayerDimensionsOptions,
  WebinarAreaDimensions,
} from './types';

export const calcWebinarPlayerDimensions = ({
  options,
  blocked,
  vimeoVideoId,
  windowArea,
  webinarAreaDiv,
  videoDimensions,
  hasPinnedInteraction,
  webinarAreaDimensionsCache,
}: {
  blocked: boolean;
  vimeoVideoId: number | null;
  hasPinnedInteraction: boolean;
  videoDimensions: VideoDimensions;
  options: VideoPlayerDimensionsOptions;
  webinarAreaDiv: HTMLDivElement | undefined;
  windowArea: { width: number; height: number };
  webinarAreaDimensionsCache: WebinarAreaDimensions;
}): WebinarDimensions => {
  const actionsPaneLocation = ![PlayerMode.live, PlayerMode.replay].includes(options.mode)
    ? 'none'
    : isMobile && isPortraitOrientation()
      ? 'bottom'
      : 'right';

  const webinarArea = {
    measured: webinarAreaDimensionsCache.measured,
    height: options.isCollapsedEmbeddedPlayer
      ? options.embeddedPlayerInfo.height
      : webinarAreaDimensionsCache.height,
    width: options.isCollapsedEmbeddedPlayer
      ? options.embeddedPlayerInfo.width
      : webinarAreaDimensionsCache.width,
  };

  const pinnedCard = { ...deepCopy(staticWebinarDimensions.actionsPane.pinnedCard) };
  const actionsPane: ActionsPaneDimensions = {
    ...deepCopy(staticWebinarDimensions.actionsPane),
    marginTopPx: 5,
    idock: {
      ...deepCopy(staticWebinarDimensions.actionsPane.idock),
      top: 0,
      iconsTop: 6 /* Leave room for badge */, // isMobile ? 50 : 75, // Make room for ScoreSpinner
      paddingLeftRem: actionsPaneLocation === 'bottom' ? 0.5 : isMobile ? 0.5 : 2,
      paddingRightRem: actionsPaneLocation === 'bottom' ? 0.25 : 0.5,
    },
    icard: {
      ...deepCopy(staticWebinarDimensions.actionsPane.icard),
      centerTopPercent: 10,
      marginYpx: staticWebinarDimensions.actionsPane.icard.marginYpx,
    },
    pinnedCard,
  };

  actionsPane.icard.widthRem =
    actionsPaneLocation === 'bottom'
      ? window.innerWidth / 14 -
        actionsPane.idock.widthRem -
        actionsPane.idock.paddingRightRem -
        actionsPane.idock.paddingLeftRem
      : isMobile
        ? 18
        : 25;

  actionsPane.actionsXPx = options.isCollapsedEmbeddedPlayer ? actionsPane.maxWidthRem * 14 : 0;

  if (options.editMode && options.editMode !== 'edit') {
    actionsPane.idock.widthRem = 0;
    actionsPane.idock.paddingRightRem = 0;

    if (options.editMode !== 'dash.interactions') {
      actionsPane.icard.paddingLeftRem = 0;
      actionsPane.idock.paddingLeftRem = 0;
      actionsPane.icard.widthRem = 0;
    }
  } else {
    if (
      (!options.editMode?.startsWith('edit') && options.actionsPaneState === 'offscreen') ||
      options.isCollapsedEmbeddedPlayer
    ) {
      actionsPane.idock.widthRem = 0;
      actionsPane.idock.paddingRightRem = 0;
    }
  }

  actionsPane.bottomWidthRem =
    actionsPane.idock.widthRem +
    actionsPane.idock.paddingLeftRem +
    actionsPane.icard.widthRem +
    actionsPane.icard.paddingLeftRem;
  actionsPane.maxWidthRem = actionsPaneLocation === 'bottom' ? 0 : actionsPane.bottomWidthRem;
  actionsPane.rightWidthRem =
    !options.editMode?.startsWith('edit') &&
    (actionsPaneLocation === 'bottom' ||
      options.actionsPaneState === 'offscreen' ||
      options.isCollapsedEmbeddedPlayer)
      ? 0
      : options.actionsPaneState === 'hidden'
        ? actionsPane.idock.widthRem + actionsPane.idock.paddingLeftRem
        : actionsPane.maxWidthRem;
  if (options?.editMode === 'dash.interactions') {
    actionsPane.rightWidthRem = actionsPane.maxWidthRem;
  }

  const videoControlsArea = {
    ...deepCopy(staticWebinarDimensions.videoControlsArea),

    width: webinarArea.width - actionsPane.rightWidthRem * 14,
    height: webinarArea.height,

    bottomBar: {
      height:
        (!options.showAgenda && !options.hasVideoPlayer) ||
        actionsPaneLocation === 'bottom' || // On portrait show controls over video
        options.isCollapsedEmbeddedPlayer
          ? 0
          : staticWebinarDimensions.videoControlsArea.bottomBar.height,
      paddingY:
        actionsPaneLocation === 'bottom'
          ? 0
          : staticWebinarDimensions.videoControlsArea.bottomBar.paddingY,
      paddingX:
        options.editMode === 'dash.chat'
          ? 0
          : staticWebinarDimensions.videoControlsArea.bottomBar.paddingX,
    },
  };

  if (options.editMode && webinarAreaDiv) {
    const videoControlsAreaDiv = webinarAreaDiv.getElementsByClassName('VideoControlsArea')[0];

    if (videoControlsAreaDiv) {
      videoControlsArea.height = videoControlsAreaDiv.clientHeight;
    }
  }

  videoControlsArea.topBar.height =
    hasPinnedInteraction && options.hasInteractions && !options.isCollapsedEmbeddedPlayer
      ? staticWebinarDimensions.videoControlsArea.topBar.height *
          Math.min(1, Math.max(0.33, videoControlsArea.height / 400)) +
        (actionsPaneLocation === 'right' && isMobile ? 2 : 0) // A little Y margin around pinned card when landscape
      : 0;

  if (options.editMode === 'dash.chat') {
    // Hide Pinned interaction on dashboard chat
    videoControlsArea.topBar.height = 0;
    // Minimize bars
    videoControlsArea.bottomBar.height = 42;
    videoControlsArea.bottomBar.paddingY = 8;
    videoControlsArea.bottomBar.paddingX = 7;
  }

  if (actionsPaneLocation === 'bottom' && options.hasChat) {
    // Portrait mobile
    const perfectHeight =
      webinarArea.width / videoDimensions.aspectRatio +
      videoControlsArea.topBar.height +
      videoControlsArea.bottomBar.height;

    videoControlsArea.height = Math.min(
      perfectHeight,
      screen.height * videoControlsArea.portraitRatio
    );
  }

  actionsPane.heightPx =
    actionsPaneLocation === 'bottom'
      ? windowArea.height - videoControlsArea.height - staticWebinarDimensions.chat.heightPx
      : webinarArea.height - staticWebinarDimensions.chat.heightPx;

  actionsPane.snappedCardTopPx =
    (options.editMode && options.editMode !== 'edit') ||
    (options.editMode === 'edit' && !options.isPlaying)
      ? 0
      : isMobile
        ? actionsPaneLocation === 'right'
          ? 5
          : 25
        : options.isCollapsedEmbeddedPlayer
          ? (options.embeddedPlayerInfo.height * 7) / 100
          : 80;
  actionsPane.heightPx -= actionsPane.snappedCardTopPx;

  const screenDimensions = {
    height:
      isMobile && !isPortraitOrientation() ? Math.min(screen.width, screen.height) : screen.height,
    width:
      isMobile && !isPortraitOrientation() ? Math.max(screen.width, screen.height) : screen.width,
  };

  // We do all calculations based on the maximum width or height of the video if we were to go massive fullscreen
  // We do this so that the video quality is the highest possible and deal with fitting into a smaller window
  // by using scale() which is hardware accelerated
  const videoStageMaxHeight = Math.min(
    screenDimensions.height,
    screenDimensions.width / videoDimensions.aspectRatio
  );
  const videoStageMaxWidth = Math.min(
    screenDimensions.width,
    screenDimensions.height * videoDimensions.aspectRatio
  );

  const videoArea = {
    top: videoControlsArea.topBar.height,
    width: videoControlsArea.width,
    height:
      videoControlsArea.height -
      videoControlsArea.topBar.height -
      videoControlsArea.bottomBar.height,
    aspectRatio: 0,
  };
  videoArea.aspectRatio = videoArea.width / videoArea.height;

  const roomArea = {
    top: videoControlsArea.topBar.height,
    width: videoControlsArea.width,
    height: videoControlsArea.height - videoControlsArea.topBar.height,
    aspectRatio: 0,
  };
  roomArea.aspectRatio = roomArea.width / roomArea.height;
  if (options.mode === PlayerMode.replay) {
    // In replay the scrobbler bar is in the way - don't push the footer down
    roomArea.height = videoArea.height;
  }

  const videoStageScale = Math.max(
    videoStageMaxWidth / videoArea.width,
    videoStageMaxHeight / videoArea.height
  );

  // VideoStage Padded dimensions take into account top / bottom bars the video should not
  // be under as well as leaving some white space around the video in all but mobile portrait
  let videoStagePaddedHeight = videoStageMaxHeight;
  let videoStagePaddedWidth = videoStageMaxWidth;
  let reduceByPercent = 0;

  // Make room for top controls unless we have enough room in video area
  const videoAreaHeightLeft = videoControlsArea.height - videoStageMaxHeight / videoStageScale;
  if (videoAreaHeightLeft < 0) {
    reduceByPercent += (Math.abs(videoAreaHeightLeft) * videoStageScale) / videoStageMaxHeight;
  }
  if (!isMobile) {
    // If we aren't portrait on mobile then add some white space around
    // the video
    reduceByPercent += 0.04;
  } else {
    if (actionsPaneLocation === 'right') {
      reduceByPercent += 0.01;
    }
  }
  if (options.isCollapsedEmbeddedPlayer) {
    reduceByPercent = 0;
  }
  reduceByPercent = Math.round(reduceByPercent * 100) / 100;
  videoStagePaddedHeight *= 1 - reduceByPercent;
  videoStagePaddedWidth *= 1 - reduceByPercent;

  const videoStage = {
    width: videoStagePaddedWidth,
    height: videoStagePaddedHeight,
    scale: videoStageScale,
    radius:
      actionsPaneLocation === 'bottom'
        ? 0
        : (options.borderRadius / 50) * videoStagePaddedWidth * 0.02,

    unscaled: {
      width: videoStagePaddedWidth / videoStageScale,
      height: videoStagePaddedHeight / videoStageScale,
    },
  };

  actionsPane.actionsTopPx =
    actionsPaneLocation === 'bottom'
      ? videoControlsArea.height + actionsPane.marginTopPx
      : (options.editMode && options.editMode !== 'edit') ||
          (options.editMode === 'edit' && !options.isPlaying)
        ? 0
        : options.isCollapsedEmbeddedPlayer
          ? actionsPane.marginTopPx
          : videoControlsArea.height < 700
            ? actionsPane.marginTopPx
            : /* Keep top aligned with video stage area */
              Math.max(
                actionsPane.marginTopPx,
                videoControlsArea.topBar.height -
                  actionsPane.snappedCardTopPx +
                  (videoArea.height - videoStage.unscaled.height) / 2
              );

  if (!isMobile) {
    actionsPane.idock.top = -actionsPane.actionsTopPx;
  }
  const staticChat = { ...deepCopy(staticWebinarDimensions.chat) };

  return {
    ...deepCopy(staticWebinarDimensions),

    isMeasured:
      config.noNetwork ||
      (webinarArea.measured && (videoDimensions.measured || !vimeoVideoId)) ||
      blocked,
    webinarArea,
    video: videoDimensions,
    videoControlsArea,
    roomArea,
    videoArea,
    videoStage,
    windowArea,
    chat: {
      ...staticChat,
      heightPx: options.isEntireChatOff ? 0 : staticChat.heightPx,
      widthRem: actionsPane.icard.widthRem + 2,
    },
    actionsPane: {
      ...deepCopy(actionsPane),
      location: actionsPaneLocation,
      pinnedCard: {
        ...actionsPane.pinnedCard,
        radius:
          actionsPaneLocation === 'bottom' || options.borderRadius === 0
            ? 0
            : Math.min(videoStage.radius / videoStage.scale, actionsPane.pinnedCard.height * 0.33),

        scale: Math.min(videoStagePaddedWidth / windowArea.width, 1),
      },
    },
  };
};
